import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { balanceApi } from '../../services/api';

interface LeaveBalance {
  leaveType: string;
  year: number;
  total: number;
  taken: number;
  approved: number;
  pending: number;
  available: number;
  accumulatedHours: number;
  cycleStart?: string;
  cycleEnd?: string;
  withMedicalCert?: number;
  withoutMedicalCert?: number;
}

export default function MyBalancesPage() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [balances, setBalances] = useState<LeaveBalance[]>([]);

  useEffect(() => {
    const fetchBalances = async () => {
      if (!user?.userId) return;

      setIsLoading(true);
      setError('');

      try {
        const response = await balanceApi.getBalance(user.userId);
        setBalances((response as { balances: LeaveBalance[] }).balances || []);
      } catch (err: any) {
        console.error('Error fetching balances:', err);
        setError(err.message || 'Failed to load balances');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBalances();
  }, [user?.userId]);

  const getBalance = (type: string): LeaveBalance | null => {
    return balances.find(b => b.leaveType === type) || null;
  };

  const annualBalance = getBalance('ANNUAL');
  const sickBalance = getBalance('SICK');
  const familyBalance = getBalance('FAMILY');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading balances...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (balances.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Leave Balances</h1>
          <p className="text-sm text-gray-600">View your leave allocations and usage</p>
        </div>
        <div className="card p-6 text-center text-gray-500">
          No leave balances have been initialized yet. Please contact your administrator.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Leave Balances</h1>
        <p className="text-sm text-gray-600">View your leave allocations and usage</p>
      </div>

      {/* Annual Leave */}
      {annualBalance && (
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Annual Leave</h2>
          <div className="mb-4">
            <div className="flex justify-between text-sm">
              <span>Used: {annualBalance.taken} days</span>
              <span>Available: {annualBalance.available} days</span>
            </div>
            <div className="mt-2 h-4 overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full bg-primary-600"
                style={{
                  width: `${annualBalance.total > 0 ? ((annualBalance.total - annualBalance.available) / annualBalance.total) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
          <div className="grid gap-4 text-sm md:grid-cols-4">
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-gray-500">Total Allocation</p>
              <p className="text-xl font-bold">{annualBalance.total} days</p>
            </div>
            <div className="rounded-lg bg-green-50 p-3">
              <p className="text-gray-500">Taken</p>
              <p className="text-xl font-bold text-green-600">{annualBalance.taken} days</p>
            </div>
            <div className="rounded-lg bg-yellow-50 p-3">
              <p className="text-gray-500">Pending</p>
              <p className="text-xl font-bold text-yellow-600">{annualBalance.pending} days</p>
            </div>
            <div className="rounded-lg bg-blue-50 p-3">
              <p className="text-gray-500">Accumulated Hours</p>
              <p className="text-xl font-bold text-blue-600">{annualBalance.accumulatedHours || 0} hrs</p>
              <p className="text-xs text-gray-500">8 hrs = 1 day</p>
            </div>
          </div>
        </div>
      )}

      {/* Sick Leave */}
      {sickBalance && (
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Sick Leave</h2>
          {sickBalance.cycleStart && sickBalance.cycleEnd && (
            <p className="mb-2 text-sm text-gray-500">
              3-Year Cycle: {sickBalance.cycleStart} to {sickBalance.cycleEnd}
            </p>
          )}
          <div className="mb-4">
            <div className="flex justify-between text-sm">
              <span>Used: {sickBalance.taken} days</span>
              <span>Available: {sickBalance.available} days</span>
            </div>
            <div className="mt-2 h-4 overflow-hidden rounded-full bg-gray-200">
              <div
                className="h-full bg-red-500"
                style={{
                  width: `${sickBalance.total > 0 ? (sickBalance.taken / sickBalance.total) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
          <div className="grid gap-4 text-sm md:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-gray-500">Total (3-year cycle)</p>
              <p className="text-xl font-bold">{sickBalance.total} days</p>
            </div>
            <div className="rounded-lg bg-green-50 p-3">
              <p className="text-gray-500">With Medical Certificate</p>
              <p className="text-xl font-bold text-green-600">{sickBalance.withMedicalCert || 0} days</p>
            </div>
            <div className="rounded-lg bg-orange-50 p-3">
              <p className="text-gray-500">Without Certificate</p>
              <p className="text-xl font-bold text-orange-600">{sickBalance.withoutMedicalCert || 0} days</p>
            </div>
          </div>
        </div>
      )}

      {/* Family Responsibility Leave */}
      {familyBalance && (
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Family Responsibility Leave</h2>
          <div className="grid gap-4 text-sm md:grid-cols-3">
            <div className="rounded-lg bg-gray-50 p-3">
              <p className="text-gray-500">Total Allocation</p>
              <p className="text-xl font-bold">{familyBalance.total} days</p>
            </div>
            <div className="rounded-lg bg-green-50 p-3">
              <p className="text-gray-500">Taken</p>
              <p className="text-xl font-bold text-green-600">{familyBalance.taken} days</p>
            </div>
            <div className="rounded-lg bg-blue-50 p-3">
              <p className="text-gray-500">Available</p>
              <p className="text-xl font-bold text-blue-600">{familyBalance.available} days</p>
            </div>
          </div>
        </div>
      )}

      {/* Other Leave Types */}
      {balances.filter(b => !['ANNUAL', 'SICK', 'FAMILY'].includes(b.leaveType)).length > 0 && (
        <div className="card p-6">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Other Leave</h2>
          <div className="grid gap-4 text-sm md:grid-cols-2">
            {balances
              .filter(b => !['ANNUAL', 'SICK', 'FAMILY'].includes(b.leaveType))
              .map(balance => (
                <div key={balance.leaveType} className="rounded-lg bg-gray-50 p-3">
                  <p className="text-gray-500">{balance.leaveType.charAt(0) + balance.leaveType.slice(1).toLowerCase()} Leave</p>
                  <p className="text-xl font-bold">
                    {balance.taken} / {balance.total} days
                  </p>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
